import { useTranslation } from 'react-i18next';
import { IconCaret } from '@components/icons';
import { Tooltip } from '@components/tooltip';
import { PopoverTextWrapper } from '@components/tooltip/src/component/popover-text-wrapper';
import {
  ADMISSION_STATUS_HIDDEN,
  ADMISSION_STATUS_HIDDEN_BY_TRIGGER,
  ADMISSION_STATUS_IN_REVIEW,
  ADMISSION_STATUS_ON_SALE,
  ADMISSION_STATUS_SALES_CLOSED,
  ADMISSION_STATUS_SOLD_OUT,
  type Status,
} from '@domain/admission';
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import classNames from 'classnames';

import { type AdmissionStatusSelectProps } from './admission-status-select.type';
import {
  ADMISSION_STATUS_SELECT_SIZE_L,
  ADMISSION_STATUS_SELECT_SIZE_S,
  ADMISSION_STATUS_SELECT_TEST_ID,
} from './constants';

export { ADMISSION_STATUS_SELECT_TEST_ID };

const STATUS_CONFIG: Record<Status, { labelKey: string; textClassName: string }> = {
  [ADMISSION_STATUS_ON_SALE]: { labelKey: 'common:admissionStatusOnSale', textClassName: 'text-success-no-bg' },
  [ADMISSION_STATUS_SALES_CLOSED]: {
    labelKey: 'common:admissionStatusSalesEnded',
    textClassName: 'text-warning-no-bg',
  },
  [ADMISSION_STATUS_SOLD_OUT]: { labelKey: 'common:admissionStatusSoldOut', textClassName: 'text-critical-no-bg' },
  [ADMISSION_STATUS_HIDDEN]: { labelKey: 'common:admissionStatusHidden', textClassName: 'text-primary' },
  [ADMISSION_STATUS_HIDDEN_BY_TRIGGER]: {
    labelKey: 'common:admissionStatusHiddenByTrigger',
    textClassName: 'text-primary',
  },
  [ADMISSION_STATUS_IN_REVIEW]: { labelKey: 'common:admissionStatusHidden', textClassName: 'text-primary' },
};

const DEFAULT_SELECTABLE_STATUSES: Status[] = [
  ADMISSION_STATUS_ON_SALE,
  ADMISSION_STATUS_SALES_CLOSED,
  ADMISSION_STATUS_SOLD_OUT,
  ADMISSION_STATUS_HIDDEN,
];

export const AdmissionStatusSelect = ({
  value,
  onChange,
  size = ADMISSION_STATUS_SELECT_SIZE_S,
  disabled = false,
  disabledReason,
  selectableStatuses = DEFAULT_SELECTABLE_STATUSES,
  dataTestId = ADMISSION_STATUS_SELECT_TEST_ID,
}: AdmissionStatusSelectProps): JSX.Element => {
  const { t } = useTranslation();
  const current = STATUS_CONFIG[value];

  const triggerClassName = classNames(
    'flex items-center gap-4 rounded border border-soft bg-primary pl-4 pr-3',
    size === ADMISSION_STATUS_SELECT_SIZE_L ? 'h-14' : 'h-10'
  );
  const label = (
    <span className={classNames('text-subtitle-m whitespace-nowrap', current?.textClassName)}>
      {t(current?.labelKey ?? '')}
    </span>
  );

  if (disabled && disabledReason) {
    return (
      <Tooltip
        ariaLabel={disabledReason}
        buttonTestId={dataTestId}
        anchorProps={{ to: 'bottom end', gap: 8 }}
        buttonClasses={classNames(triggerClassName, 'cursor-not-allowed opacity-50')}
        button={
          <>
            {label}
            <IconCaret className='size-5 shrink-0 text-primary rotate-90' />
          </>
        }
        popoverContent={<PopoverTextWrapper>{disabledReason}</PopoverTextWrapper>}
      />
    );
  }

  return (
    <Menu as='div' className='relative'>
      {({ open }) => (
        <>
          <MenuButton
            disabled={disabled}
            data-testid={dataTestId}
            className={classNames(triggerClassName, { 'cursor-not-allowed opacity-50': disabled })}>
            {label}
            <IconCaret
              className={classNames(
                'size-5 shrink-0 text-primary transition-transform',
                open ? '-rotate-90' : 'rotate-90'
              )}
            />
          </MenuButton>

          <Transition
            show={open}
            as='div'
            enter='transition ease-out duration-100'
            enterFrom='opacity-0 scale-95'
            enterTo='opacity-100 scale-100'
            leave='transition ease-in duration-75'
            leaveFrom='opacity-100 scale-100'
            leaveTo='opacity-0 scale-95'>
            <MenuItems
              modal={false}
              anchor={{ to: 'bottom end', gap: 8 }}
              className='z-60 w-max origin-top-right overflow-auto rounded border border-soft bg-primary shadow-md focus:outline-hidden'>
              {selectableStatuses.map((status) => {
                const config = STATUS_CONFIG[status];
                return (
                  <MenuItem key={status}>
                    <button
                      type='button'
                      className='flex w-full items-center px-6 py-3 hover:bg-extra-soft'
                      onClick={() => onChange(status)}>
                      <span
                        className={classNames('text-subtitle-m whitespace-nowrap text-left', config?.textClassName)}>
                        {t(config?.labelKey ?? '')}
                      </span>
                    </button>
                  </MenuItem>
                );
              })}
            </MenuItems>
          </Transition>
        </>
      )}
    </Menu>
  );
};
